<template>
  <div class="saved-connections-management">
    <!-- Modern Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <div class="icon-wrapper">
            <Database :size="32" />
          </div>
          <div>
            <h1>API Connections</h1>
            <p class="subtitle">Manage your data connections</p>
          </div>
        </div>
        <button @click="$emit('initiate-new-connection')" class="btn-primary">
          <Plus :size="18" />
          New Connection
        </button>
      </div>

      <!-- Stats Bar -->
      <div v-if="savedConnectionsList.length > 0" class="stats-bar">
        <div class="stat-item">
          <Link :size="16" />
          <span class="stat-value">{{ savedConnectionsList.length }}</span>
          <span class="stat-label">Active Connections</span>
        </div>
        <div class="stat-item">
          <Zap :size="16" />
          <span class="stat-value">{{ totalEndpoints }}</span>
          <span class="stat-label">Total Endpoints</span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <Transition name="fade">
      <div v-if="isProcessingRequest" class="loading-state">
        <Loader2 :size="48" class="spin" />
        <p>Loading your connections...</p>
      </div>
    </Transition>

    <!-- Empty State -->
    <Transition name="fade">
      <div v-if="!isProcessingRequest && savedConnectionsList.length === 0" class="empty-state">
        <div class="empty-icon">
          <Database :size="80" />
        </div>
        <h2>No Connections Yet</h2>
        <p>Connect to your first API to start building data pipelines</p>
        <button @click="$emit('initiate-new-connection')" class="btn-primary btn-large">
          <Plus :size="20" />
          Create Your First Connection
        </button>
      </div>
    </Transition>

    <!-- Connections Grid -->
    <Transition name="fade">
      <div v-if="!isProcessingRequest && savedConnectionsList.length > 0" class="connections-grid">
        <div
          v-for="connection in savedConnectionsList"
          :key="connection.id"
          class="connection-card"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="connection-icon">
              <Globe :size="24" />
            </div>
            <div class="card-header-content">
              <h3>{{ connection.name }}</h3>
              <span class="connection-url">{{ connection.baseUrl }}</span>
            </div>

            <!-- Actions Menu -->
            <DropdownMenuRoot>
              <DropdownMenuTrigger class="menu-trigger">
                <MoreVertical :size="20" />
              </DropdownMenuTrigger>

              <DropdownMenuPortal>
                <DropdownMenuContent class="dropdown-content" :side-offset="5">
                  <DropdownMenuItem
                    class="dropdown-item"
                    @select="executeConnectionValidationTest(connection)"
                  >
                    <Shield :size="16" />
                    <span>Test Connection</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    class="dropdown-item"
                    @select="retrieveEndpointSampleData(connection)"
                  >
                    <Eye :size="16" />
                    <span>Preview Data</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator class="dropdown-separator" />

                  <DropdownMenuItem
                    class="dropdown-item danger"
                    @select="initiateConnectionRemoval(connection)"
                  >
                    <Trash2 :size="16" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenuRoot>
          </div>

          <!-- Card Body -->
          <div class="card-body">
            <!-- Auth Badge -->
            <div class="info-row">
              <div class="badge" :class="getAuthBadgeClass(connection.authConfig?.type || connection.authType)">
                <Lock :size="14" />
                <span>{{ formatAuthenticationMethodDisplay(connection.authConfig?.type || connection.authType) }}</span>
              </div>
            </div>

            <!-- Endpoints -->
            <div v-if="connection.selectedEndpoints?.length" class="endpoints-section">
              <div class="section-label">
                <Route :size="14" />
                <span>Endpoints ({{ connection.selectedEndpoints.length }})</span>
              </div>
              <div class="endpoint-tags">
                <span
                  v-for="(endpoint, idx) in connection.selectedEndpoints.slice(0, 3)"
                  :key="idx"
                  class="endpoint-tag"
                >
                  <span class="method">{{ endpoint.method }}</span>
                  {{ endpoint.path }}
                </span>
                <span v-if="connection.selectedEndpoints.length > 3" class="more-tag">
                  +{{ connection.selectedEndpoints.length - 3 }} more
                </span>
              </div>
            </div>
          </div>

          <!-- Card Footer -->
          <div class="card-footer">
            <div class="audit-info">
              <div class="audit-item">
                <Clock :size="12" />
                <span class="audit-text"
                  >Created {{ formatRelativeTime(connection.createdAt) }}</span
                >
              </div>
              <div v-if="connection.updatedAt" class="audit-item">
                <RefreshCw :size="12" />
                <span class="audit-text"
                  >Updated {{ formatRelativeTime(connection.updatedAt) }}</span
                >
              </div>
            </div>
            <div class="footer-actions">
              <button
                @click="executeConnectionValidationTest(connection)"
                :disabled="isValidatingConnection"
                class="btn-secondary btn-small"
              >
                <Shield :size="14" />
                Test
              </button>
              <button
                @click="retrieveEndpointSampleData(connection)"
                :disabled="isProcessingRequest"
                class="btn-secondary btn-small"
              >
                <Eye :size="14" />
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete Confirmation Modal -->
    <Transition name="modal">
      <div
        v-if="pendingDeleteConnection"
        class="modal-backdrop"
        @click.self="pendingDeleteConnection = null"
      >
        <div class="modal-container modal-container-sm">
          <div class="modal-header">
            <div class="modal-title">
              <Trash2 :size="24" class="modal-title-danger" />
              <h3>Delete Connection</h3>
            </div>
            <button @click="pendingDeleteConnection = null" class="modal-close">
              <X :size="20" />
            </button>
          </div>
          <div class="modal-body">
            <p class="confirm-message">
              Are you sure you want to delete
              <strong>"{{ pendingDeleteConnection.name }}"</strong>?
              This action cannot be undone.
            </p>
          </div>
          <div class="modal-footer">
            <button @click="pendingDeleteConnection = null" class="btn-secondary">
              Cancel
            </button>
            <button @click="confirmDeleteConnection" class="btn-danger-solid">
              <Trash2 :size="14" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Sample Data Modal -->
    <Transition name="modal">
      <div
        v-if="sampleDataPreviewModal"
        class="modal-backdrop"
        @click.self="sampleDataPreviewModal = null"
      >
        <div class="modal-container">
          <div class="modal-header">
            <div class="modal-title">
              <FileJson :size="24" />
              <h3>Sample Data Preview</h3>
            </div>
            <button @click="sampleDataPreviewModal = null" class="modal-close">
              <X :size="20" />
            </button>
          </div>

          <div class="modal-body">
            <div v-if="sampleDataPreviewModal.columns?.length" class="data-info">
              <div class="info-badge">
                <Database :size="16" />
                <span>{{ sampleDataPreviewModal.totalRecords || sampleDataPreviewModal.records.length }} records</span>
              </div>
              <div class="info-badge">
                <Columns :size="16" />
                <span>{{ sampleDataPreviewModal.columns.length }} columns</span>
              </div>
            </div>

            <div v-if="sampleDataPreviewModal.columns?.length" class="columns-list">
              <h4>Columns</h4>
              <div class="column-tags">
                <span v-for="col in sampleDataPreviewModal.columns" :key="col" class="column-tag">
                  {{ col }}
                </span>
              </div>
            </div>

            <div v-if="sampleDataPreviewModal.records?.length" class="records-section">
              <h4>Sample Records</h4>
              <pre class="code-block">{{
                JSON.stringify(sampleDataPreviewModal.records, null, 2)
              }}</pre>
            </div>

            <div v-else class="empty-data">
              <AlertCircle :size="48" />
              <p>No sample records available</p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import {
  Database,
  Plus,
  Link,
  Zap,
  Loader2,
  Globe,
  MoreVertical,
  Shield,
  Eye,
  Trash2,
  Lock,
  Route,
  FileJson,
  X,
  Columns,
  AlertCircle,
  Clock,
  RefreshCw,
} from "lucide-vue-next";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "radix-vue";
import apiService from "../services/api.js";
import { useNotification } from "../composables/useNotification";

const { success, error } = useNotification();

const emit = defineEmits(['initiate-new-connection']);

const savedConnectionsList = ref([]);
const isProcessingRequest = ref(false);
const isValidatingConnection = ref(false);
const sampleDataPreviewModal = ref(null);
const pendingDeleteConnection = ref(null);

const totalEndpoints = computed(() => {
  return savedConnectionsList.value.reduce((sum, conn) => {
    return sum + (conn.selectedEndpoints?.length || 0);
  }, 0);
});

onMounted(async () => {
  await loadUserConnectionsList();
});

const loadUserConnectionsList = async () => {
  isProcessingRequest.value = true;
  try {
    const connectionsResponse = await apiService.retrieveUserConnections();
    savedConnectionsList.value = connectionsResponse.data;
  } catch (requestError) {
    const errorMessage = requestError && typeof requestError === 'object' && 'errorMessage' in requestError
      ? requestError.errorMessage
      : "Could not load connections";
    error("Failed to Load", errorMessage);
  }
  isProcessingRequest.value = false;
};

const initiateConnectionRemoval = (connectionConfiguration) => {
  pendingDeleteConnection.value = connectionConfiguration;
};

const confirmDeleteConnection = async () => {
  const connectionConfiguration = pendingDeleteConnection.value;
  if (!connectionConfiguration) return;
  pendingDeleteConnection.value = null;

  try {
    await apiService.removeConnection(connectionConfiguration.id);
    savedConnectionsList.value = savedConnectionsList.value.filter(
      (connection) => connection.id !== connectionConfiguration.id
    );
    success("Connection Deleted", `"${connectionConfiguration.name}" has been removed.`);
  } catch (requestError) {
    const errorMessage = requestError && typeof requestError === 'object' && 'errorMessage' in requestError
      ? requestError.errorMessage
      : "Could not delete connection";
    error("Delete Failed", errorMessage);
  }
};

const executeConnectionValidationTest = async (connectionConfiguration) => {
  isValidatingConnection.value = true;
  try {
    await apiService.validateAuthentication(
      connectionConfiguration.baseUrl,
      connectionConfiguration.authConfig || {} 
    );
    success("Connection Valid", `"${connectionConfiguration.name}" is working correctly`);
  } catch (requestError) {
    const errorMessage = requestError && typeof requestError === 'object' && 'errorMessage' in requestError
      ? requestError.errorMessage
      : "Connection test failed";
    error("Connection Failed", errorMessage);
  }
  isValidatingConnection.value = false;
};

const retrieveEndpointSampleData = async (connectionConfiguration) => {
  if (!connectionConfiguration.selectedEndpoints?.length) {
    error("No Endpoints", "No endpoints are configured for this connection");
    return;
  }

  isProcessingRequest.value = true;
  try {
    const firstEndpoint = connectionConfiguration.selectedEndpoints[0];
    let endpointPath = `${firstEndpoint.method} ${firstEndpoint.path}`;

    if (firstEndpoint.parameters?.length) {
      const queryParams = firstEndpoint.parameters
        .filter((p) => p.value)
        .map((p) => `${p.name}=${encodeURIComponent(p.value)}`)
        .join("&");

      if (queryParams) {
        endpointPath += `?${queryParams}`;
      }
    }

    const sampleDataResponse = await apiService.retrieveSampleDataFromEndpoint(
      connectionConfiguration.id,
      endpointPath,
      10
    );

    if (sampleDataResponse.data.success) {
      sampleDataPreviewModal.value = sampleDataResponse.data;
    } else {
      error(
        "Sample Data Failed",
        sampleDataResponse.data.errorMessage || "Failed to retrieve sample data"
      );
    }
  } catch (requestError) {
    const errorMessage = requestError && typeof requestError === 'object' && 'errorMessage' in requestError
      ? requestError.errorMessage
      : "Failed to retrieve sample data";
    error("Sample Data Failed", errorMessage);
  }
  isProcessingRequest.value = false;
};

const formatAuthenticationMethodDisplay = (authType) => {
  const names = {
    None: "No Auth",
    ApiKeyHeader: "API Key",
    BearerToken: "Bearer",
    BasicAuth: "Basic Auth",
  };
  return names[authType] || authType;
};

const getAuthBadgeClass = (authType) => {
  if (authType === "None") return "badge-neutral";
  return "badge-secure";
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 30) {
    const diffMonth = Math.floor(diffDay / 30);
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;
  }
  if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
  if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  return "just now";
};
</script>

<style scoped>
.saved-connections-management {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem;
}

/* Header */
.page-header {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.title-section h1 {
  margin: 0 0 0.25rem 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary);
}

/* Stats Bar */
.stats-bar {
  display: flex;
  gap: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-text-secondary);
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-accent);
}

.stat-label {
  font-size: 13px;
  color: var(--color-text-secondary);
}

/* Buttons */
.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-family: inherit;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
}

.btn-primary.btn-large {
  padding: 1rem 2rem;
  font-size: 16px;
}

.btn-secondary {
  background: white;
  color: var(--color-text-secondary);
  border: 2px solid rgba(0, 0, 0, 0.08);
}

.btn-secondary:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.btn-secondary.btn-small {
  padding: 0.5rem 0.875rem;
  font-size: 13px;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.loading-state p {
  margin: 1rem 0 0 0;
  color: var(--color-text-secondary);
  font-size: 15px;
}

.spin {
  animation: spin 1s linear infinite;
  color: var(--color-accent);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 6rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.empty-icon {
  width: 120px;
  height: 120px;
  margin: 0 auto 2rem;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(102, 126, 234, 0.05));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent);
}

.empty-state h2 {
  margin: 0 0 0.75rem 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.empty-state p {
  margin: 0 0 2rem 0;
  color: var(--color-text-secondary);
  font-size: 15px;
}

/* Connections Grid */
.connections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
}

/* Connection Card */
.connection-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.connection-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: var(--color-accent);
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(102, 126, 234, 0.02));
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.connection-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.card-header-content {
  flex: 1;
  min-width: 0;
}

.card-header-content h3 {
  margin: 0 0 0.25rem 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.connection-url {
  font-size: 12px;
  color: var(--color-text-secondary);
  font-family: "Monaco", "Consolas", monospace;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-trigger {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.menu-trigger:hover {
  background: rgba(0, 0, 0, 0.04);
  border-color: var(--color-accent);
  color: var(--color-accent);
}

/* Dropdown Menu */
.dropdown-content {
  background: white;
  border-radius: 10px;
  padding: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 2px solid rgba(0, 0, 0, 0.06);
  min-width: 180px;
  z-index: 9999;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: var(--color-text-primary);
  transition: all 0.2s;
  outline: none;
}

.dropdown-item:hover {
  background: rgba(102, 126, 234, 0.1);
  color: var(--color-accent);
}

.dropdown-item.danger {
  color: var(--color-error);
}

.dropdown-item.danger:hover {
  background: rgba(239, 68, 68, 0.1);
}

.dropdown-separator {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 0.5rem 0;
}

/* Card Body */
.card-body {
  padding: 1.5rem;
}

.info-row {
  margin-bottom: 1rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
}

.badge-secure {
  background: rgba(34, 197, 94, 0.1);
  color: var(--color-success);
}

.badge-neutral {
  background: rgba(0, 0, 0, 0.06);
  color: var(--color-text-secondary);
}

.endpoints-section {
  margin-top: 1rem;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
}

.endpoint-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.endpoint-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  font-size: 11px;
  font-family: "Monaco", "Consolas", monospace;
  color: var(--color-text-primary);
}

.endpoint-tag .method {
  padding: 0.125rem 0.375rem;
  background: var(--color-success);
  color: white;
  border-radius: 4px;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.5px;
}

.more-tag {
  padding: 0.375rem 0.75rem;
  background: rgba(102, 126, 234, 0.1);
  color: var(--color-accent);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

/* Card Footer */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.02);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.footer-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.audit-info {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
}

.audit-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-secondary);
}

.audit-text {
  font-size: 11px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Delete confirmation modal extras */
.modal-container-sm {
  max-width: 480px;
}

.modal-title-danger {
  color: var(--color-error);
}

.confirm-message {
  font-size: 15px;
  color: var(--color-text-primary);
  line-height: 1.6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.02);
  border-radius: 0 0 16px 16px;
}

.btn-danger-solid {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: var(--color-error);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.btn-danger-solid:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
}

.modal-container {
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-accent);
}

.modal-title h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text-secondary);
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--color-text-primary);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

.data-info {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  color: var(--color-accent);
  font-size: 14px;
  font-weight: 600;
}

.columns-list {
  margin-bottom: 1.5rem;
}

.columns-list h4,
.records-section h4 {
  margin: 0 0 1rem 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.column-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.column-tag {
  padding: 0.5rem 0.875rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  font-size: 13px;
  font-family: "Monaco", "Consolas", monospace;
  color: var(--color-text-primary);
}

.code-block {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 1.5rem;
  border-radius: 10px;
  font-size: 13px;
  font-family: "Monaco", "Consolas", monospace;
  overflow-x: auto;
  margin: 0;
  white-space: pre;
  line-height: 1.6;
}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--color-text-muted);
}

.empty-data svg {
  opacity: 0.3;
  margin-bottom: 1rem;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
